import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addPollInvite,
  deletePollInvite,
  invitePollKnownEmails,
  invitePollNewEmail,
  patchHutDuty,
  sendPollInvites,
  type HutDuty,
  type PollDetail,
  type PollParticipant,
  type SendInvitesResult,
} from '../../lib/api';
import { formatDateShort } from '../../lib/format';
import { lastDutyDate } from '../../lib/hut-duty-history';
import { todayInZurich } from '../../lib/today';
import { BTN_DANGER, BTN_PRIMARY, BTN_SECONDARY, INPUT, useErr } from './poll-ui';

export function ParticipantSection({
  detail,
  roster,
  onReload,
}: {
  detail: PollDetail;
  roster: HutDuty[];
  onReload: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const toMsg = useErr();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<number | null>(null);
  const [emailDrafts, setEmailDrafts] = useState<Record<number, string>>({});
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [resend, setResend] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendResult, setSendResult] = useState<SendInvitesResult | null>(null);
  // Per-row in-flight set. `busy` alone would grey out every checkbox on the
  // first click, and the ticked box would snap back until the reload lands.
  const [pendingNameIds, setPendingNameIds] = useState<ReadonlySet<number>>(new Set());

  const invitedByNameId = new Map(
    detail.participants.filter((p) => p.name_id != null).map((p) => [p.name_id as number, p]),
  );
  const knownWithEmail = roster.filter((r) => r.email && r.active);
  const today = todayInZurich();
  const activeClaimCount = new Map<number, number>();
  for (const cl of detail.claims) {
    if (cl.status !== 'active') continue;
    activeClaimCount.set(cl.participant_id, (activeClaimCount.get(cl.participant_id) ?? 0) + 1);
  }

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      await onReload();
    } catch (e) {
      setError(toMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveEmail = (r: HutDuty) => {
    const draft = (emailDrafts[r.id] ?? r.email ?? '').trim();
    if (draft === (r.email ?? '')) return;
    void run(() => patchHutDuty(r.id, { email: draft || null }));
  };
  // The checkbox *is* poll membership: ticking invites, unticking withdraws.
  // addPollInvite is idempotent per (poll, name_id) server-side, so a double
  // click cannot produce a second participant row.
  const toggle = (r: HutDuty) => {
    const invite = invitedByNameId.get(r.id);
    if (invite) {
      remove(invite);
      return;
    }
    setPendingNameIds((s) => new Set(s).add(r.id));
    void run(() => addPollInvite(detail.id, { name_id: r.id })).finally(() =>
      setPendingNameIds((s) => {
        const next = new Set(s);
        next.delete(r.id);
        return next;
      }),
    );
  };
  const remove = (p: PollParticipant) => {
    const name = p.resolved_name ?? t('poll.admin.unnamed');
    const claims = activeClaimCount.get(p.id) ?? 0;
    const msg =
      claims > 0
        ? t('poll.admin.confirmRemoveParticipantClaims', { name, count: claims })
        : t('poll.admin.confirmRemoveParticipant', { name });
    if (!window.confirm(msg)) {
      // React only resets a controlled input's DOM value when it re-renders, and
      // bailing out here changes no state -- so the box would stay unticked.
      setPendingNameIds((s) => new Set(s));
      return;
    }
    void run(() => deletePollInvite(detail.id, p.id));
  };
  // There is deliberately no way to delete a roster name from here. Doing so
  // cascades through hut_duty_slots and erases that person's entire duty
  // history app-wide (migration 0002) -- far beyond this screen's remit, which
  // is a single poll. Roster deletion lives in the Namensverzeichnis tab, where
  // the consequence is in view. The checkbox below is the only membership
  // control this section needs.
  const inviteAllKnown = () => void run(() => invitePollKnownEmails(detail.id));
  const addNew = () =>
    void run(async () => {
      await invitePollNewEmail(detail.id, newName.trim(), newEmail.trim());
      setNewName('');
      setNewEmail('');
    });
  const send = () =>
    void run(async () => {
      setSendResult(await sendPollInvites(detail.id, resend));
    });

  const copy = async (pid: number, token: string) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/dienst/${token}`);
      setCopied(pid);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <section className="space-y-3">
      <h3 className="font-semibold">{t('poll.admin.participants')}</h3>
      {error && (
        <p
          role="alert"
          className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2"
        >
          {error}
        </p>
      )}

      {/* Known roster + emails */}
      <div className="rounded-md border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800">
          <span className="text-xs font-medium text-slate-500">
            {t('poll.admin.knownAddresses')}
          </span>
          <button
            type="button"
            className={BTN_SECONDARY}
            disabled={busy || knownWithEmail.length === 0}
            onClick={inviteAllKnown}
          >
            {t('poll.admin.inviteAllKnown', {
              n: knownWithEmail.filter((r) => !invitedByNameId.has(r.id)).length,
            })}
          </button>
        </div>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800 max-h-80 overflow-y-auto">
          {roster.map((r) => {
            const invited = invitedByNameId.has(r.id);
            const pending = pendingNameIds.has(r.id);
            const last = lastDutyDate(r.slots, today);
            return (
              <li key={r.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                <input
                  type="checkbox"
                  className="shrink-0"
                  checked={invited || pending}
                  disabled={busy || pending}
                  aria-label={t('poll.admin.inviteToggleName', { name: r.name })}
                  onChange={() => toggle(r)}
                />
                <span className="flex-1 basis-0 min-w-0 truncate" title={r.name}>
                  {r.name}
                </span>
                <span
                  className="w-24 shrink-0 truncate text-xs text-slate-400"
                  title={t('poll.admin.lastDuty')}
                >
                  {last ? formatDateShort(last, i18n.language) : t('poll.admin.noDutyYet')}
                </span>
                <input
                  type="email"
                  placeholder={t('poll.admin.emailPlaceholder')}
                  className={`${INPUT} py-1 flex-1 basis-0 min-w-0`}
                  value={emailDrafts[r.id] ?? r.email ?? ''}
                  onChange={(e) => setEmailDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                  onBlur={() => saveEmail(r)}
                />
              </li>
            );
          })}
          {roster.length === 0 && (
            <li className="px-3 py-2 text-sm text-slate-500">{t('poll.admin.noRoster')}</li>
          )}
        </ul>
      </div>

      {/* Add a brand-new address */}
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-dashed border-slate-300 dark:border-slate-600 p-3">
        <span className="w-full text-xs font-medium text-slate-500">
          {t('poll.admin.addNewAddress')}
        </span>
        <label className="flex flex-col text-xs text-slate-500">
          {t('poll.admin.rosterName')}
          <input className={INPUT} value={newName} onChange={(e) => setNewName(e.target.value)} />
        </label>
        <label className="flex flex-col text-xs text-slate-500 flex-1 min-w-[12rem]">
          {t('poll.admin.email')}
          <input
            type="email"
            className={INPUT}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
        </label>
        <button
          type="button"
          className={BTN_PRIMARY}
          disabled={busy || !newName.trim() || !newEmail.trim()}
          onClick={addNew}
        >
          {t('poll.admin.addAndInvite')}
        </button>
      </div>

      {/* Invited participants + send */}
      <ul className="divide-y divide-slate-200 dark:divide-slate-700 rounded-md border border-slate-200 dark:border-slate-700">
        {detail.participants.map((p) => (
          <li key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm flex-wrap">
            <span>{p.resolved_name ?? t('poll.admin.unnamed')}</span>
            {p.email && <span className="text-slate-400 text-xs">{p.email}</span>}
            {p.email_sent_at ? (
              <span className="text-emerald-600 text-xs">✉ {t('poll.admin.mailSent')}</span>
            ) : p.email ? (
              <span className="text-amber-600 text-xs">✉ {t('poll.admin.mailPending')}</span>
            ) : null}
            {p.last_seen_at && (
              <span className="text-emerald-600 text-xs">● {t('poll.admin.seen')}</span>
            )}
            <button
              type="button"
              className={`${BTN_SECONDARY} ml-auto`}
              onClick={() => void copy(p.id, p.token)}
            >
              {copied === p.id ? t('poll.admin.copied') : t('poll.admin.copyLink')}
            </button>
            <button type="button" className={BTN_DANGER} disabled={busy} onClick={() => remove(p)}>
              {t('common.delete')}
            </button>
          </li>
        ))}
        {detail.participants.length === 0 && (
          <li className="px-3 py-2 text-sm text-slate-500">{t('poll.admin.noParticipants')}</li>
        )}
      </ul>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={BTN_PRIMARY} disabled={busy} onClick={send}>
          {t('poll.admin.sendInvites')}
        </button>
        <label className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
          <input type="checkbox" checked={resend} onChange={(e) => setResend(e.target.checked)} />
          {t('poll.admin.resendAll')}
        </label>
        {sendResult && (
          <span className="text-sm text-slate-600 dark:text-slate-400">
            {t('poll.admin.sendResult', {
              sent: sendResult.sent,
              skipped: sendResult.skipped,
              failed: sendResult.failed.length,
            })}
          </span>
        )}
      </div>
      {sendResult && sendResult.failed.length > 0 && (
        <ul className="text-xs text-rose-700 dark:text-rose-300">
          {sendResult.failed.map((f, i) => (
            <li key={i}>
              {f.email}: {f.error}
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-slate-500">{t('poll.admin.sendHint')}</p>
    </section>
  );
}
